import { ref } from 'vue'
import { alertEnum } from '@/enums/AlertEnum.ts'
import { useAlerts } from '@/composables/alert/useAlerts.ts'
import fetchApi from '@/utils/fetchApi.ts'
import type { DocumentItem, DocumentsSummaryResponse, Option } from 'shared/src'

const isUploading = ref(false)
const uploadError = ref<string | null>(null)
const { addAlert } = useAlerts()
const isDeleting = ref(false)
const isUpdating = ref(false)
const documentOptions = ref<Option[]>([])
const BATCH_SIZE = 5

export const useKnowledgeApi = () => {
  const uploadBatch = async (
    files: File[],
  ): Promise<{ documents_imported: number; errors_count: number; errors: string[] }> => {
    const formData = new FormData()
    for (const file of files) {
      formData.append('documents', file)
    }

    const origin = window.location.origin
    const url = new URL('/api/documents/add', origin)

    const response = await fetch(url.toString(), {
      method: 'POST',
      body: formData,
      credentials: 'include',
    })

    if (!response.ok) {
      let message = response.statusText || 'Upload failed'

      try {
        const contentType = response.headers.get('content-type') ?? ''
        if (contentType.includes('application/json')) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const data = await response.json()
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          message = (data?.message as string) || (data?.error as string) || JSON.stringify(data)
        } else {
          const text = await response.text()
          if (text) message = text
        }
      } catch (e) {
        console.error('Error parsing error response:', e)
      }
      throw new Error(message)
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return response.json()
  }

  const uploadDocuments = async (files: File[]): Promise<{ ok: boolean; count: number } | null> => {
    if (files.length === 0) {
      uploadError.value = 'No files provided'
      return null
    }

    isUploading.value = true
    uploadError.value = null

    try {
      const batches: File[][] = []
      for (let i = 0; i < files.length; i += BATCH_SIZE) {
        batches.push(files.slice(i, i + BATCH_SIZE))
      }

      let totalImported = 0
      let totalErrors = 0

      for (const batch of batches) {
        const result = await uploadBatch(batch)
        totalImported += result.documents_imported
        totalErrors += result.errors_count
      }

      return { ok: totalErrors === 0, count: totalImported }
    } catch (error) {
      uploadError.value = error instanceof Error ? error.message : 'Unknown upload error'
      console.error('Error uploading documents:', error)
      addAlert('error_occurred_try_again', alertEnum.Danger)
      return null
    } finally {
      isUploading.value = false
    }
  }

  const uploadDocument = async (file: File): Promise<{ ok: boolean; count: number } | null> => {
    return uploadDocuments([file])
  }

  const clearUploadError = () => {
    uploadError.value = null
  }

  const updateDocument = async (id: string, file: File) => {
    if (!id) {
      addAlert('missing_document_id', alertEnum.Danger)
      return null
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!file) {
      addAlert('no_file_provided', alertEnum.Danger)
      return null
    }

    isUploading.value = true
    isUpdating.value = true

    try {
      const formData = new FormData()
      formData.append('document', file)

      const origin = window.location.origin
      const url = new URL(`/api/documents/update/${id}`, origin)

      const response = await fetch(url.toString(), {
        method: 'PUT',
        body: formData,
        credentials: 'include',
      })

      const contentType = response.headers.get('content-type') ?? ''

      if (!response.ok) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const errorMessage = contentType.includes('application/json')
          ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ((await response.json())?.message ?? 'Update failed')
          : await response.text()
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        throw new Error(errorMessage)
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const data: DocumentItem = contentType.includes('application/json')
        ? await response.json()
        : ({} as DocumentItem)

      addAlert('document_updated_successfully', alertEnum.Success)
      return data
    } catch (err) {
      console.error('Error updating document:', err)
      addAlert('error_occurred_try_again', alertEnum.Danger)
      return null
    } finally {
      isUploading.value = false
      isUpdating.value = false
    }
  }

  const fetchDocumentsSummary = async (): Promise<DocumentsSummaryResponse | null> => {
    try {
      const res = await fetchApi<DocumentsSummaryResponse>({
        endpoint: '/api/documents/summary',
        method: 'GET',
      })

      documentOptions.value = res.documents.map((doc) => ({
        label: doc.name,
        value: doc.document_id,
      }))

      return res
    } catch (err) {
      console.error('Error fetching documents summary:', err)
      return null
    }
  }

  const getDocumentPreview = async (id: string): Promise<Blob | null> => {
    if (!id) {
      console.error('getDocumentPreview: missing id')
      return null
    }

    try {
      return await fetchApi<Blob>({
        endpoint: `/api/documents/preview/${id}`,
        method: 'GET',
        responseType: 'blob',
      })
    } catch (err) {
      console.error('Error fetching document preview:', err)
      return null
    }
  }

  const getDocumentPreviewUrl = async (id: string): Promise<string | null> => {
    if (!id) {
      console.error('getDocumentPreviewUrl: missing id')
      return null
    }

    try {
      const res = await fetchApi<{ url: string }>({
        endpoint: `/api/documents/preview-url/${id}`,
        method: 'GET',
      })
      return res.url
    } catch (err) {
      console.error('Error fetching document preview URL:', err)
      return null
    }
  }

  const downloadDocumentsBulk = async (ids: string[]): Promise<Blob | null> => {
    if (ids.length === 0) return null
    try {
      return await fetchApi<Blob>({
        endpoint: '/api/documents/download/bulk',
        method: 'POST',
        body: { ids },
        responseType: 'blob',
        timeout: null,
      })
    } catch (err) {
      console.error('Error downloading documents:', err)
      return null
    }
  }

  const downloadAllDocuments = async (): Promise<Blob | null> => {
    try {
      return await fetchApi<Blob>({
        endpoint: '/api/documents/download/all',
        method: 'GET',
        responseType: 'blob',
        timeout: null,
      })
    } catch (err) {
      console.error('Error downloading all documents:', err)
      return null
    }
  }

  return {
    uploadDocuments,
    uploadDocument,
    isUploading,
    uploadError,
    clearUploadError,
    fetchDocumentsSummary,
    documentOptions,
    isDeleting,
    updateDocument,
    getDocumentPreview,
    getDocumentPreviewUrl,
    downloadDocumentsBulk,
    downloadAllDocuments,
  }
}
